import React, { useState } from 'react'
import { Box } from '@mui/material'
import { ChevronDownIcon, UserGroupIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../constants/colors'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { SprintTeamDetail } from '../../types/TrainingSprintTypes'

/** Optional copy overrides (e.g. gamer `gamer.upcomingActivities.teamsSection.*` vs facilitator defaults). */
export type SprintTeamMembersCollapsibleLabels = {
  teamsAndParticipants: string
  teams: string
  members: string
  noMembersAssigned: string
  unassigned: string
}

export interface SprintTeamMembersCollapsibleProps {
  teamDetails: SprintTeamDetail[]
  unassignedMemberNames?: string[]
  defaultExpanded?: boolean
  /** When set, merged with facilitator default strings from facilitator sprintCard translation keys. */
  labels?: Partial<SprintTeamMembersCollapsibleLabels>
}

const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase())
    .join('')
    .slice(0, 2)

const totalMemberCount = (teams: SprintTeamDetail[], unassigned: string[]): number =>
  teams.reduce((sum, t) => sum + t.memberNames.length, 0) + unassigned.length

export const SprintTeamMembersCollapsible: React.FC<SprintTeamMembersCollapsibleProps> = ({
  teamDetails,
  unassignedMemberNames = [],
  defaultExpanded = false,
  labels
}) => {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(defaultExpanded)

  const L = {
    teamsAndParticipants: labels?.teamsAndParticipants ?? t('facilitator.sprintCard.teamsAndParticipants'),
    teams: labels?.teams ?? t('facilitator.sprintCard.teams'),
    members: labels?.members ?? t('facilitator.sprintCard.members'),
    noMembersAssigned: labels?.noMembersAssigned ?? t('facilitator.sprintCard.noMembersAssigned'),
    unassigned: labels?.unassigned ?? t('facilitator.sprintCard.unassigned')
  }

  const totalMembers = totalMemberCount(teamDetails, unassignedMemberNames)
  const hasContent = teamDetails.length > 0 || unassignedMemberNames.length > 0

  if (!hasContent) return null

  return (
    <Box
      sx={{
        borderTop: `1px solid ${INTERAX_COLORS.white10}`,
        mt: 1
      }}
    >
      {/* Toggle header */}
      <Box
        component="button"
        type="button"
        aria-expanded={isExpanded}
        onClick={() => setIsExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          py: 1.5,
          px: 0,
          gap: 1,
          '&:hover': { opacity: 0.8 }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UserGroupIcon style={{ width: 16, height: 16, color: INTERAX_COLORS.sun, flexShrink: 0 }} />
          <Box className="text-body-3" sx={{ color: INTERAX_COLORS.sun, fontWeight: 500 }}>
            {L.teamsAndParticipants}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              backgroundColor: INTERAX_COLORS.sun20,
              borderRadius: '12px',
              px: 1,
              py: 0.25
            }}
          >
            <Box className="text-body-3" sx={{ color: INTERAX_COLORS.sun, whiteSpace: 'nowrap' }}>
              {teamDetails.length} {L.teams} · {totalMembers} {L.members}
            </Box>
          </Box>
          <ChevronDownIcon
            style={{
              width: 14,
              height: 14,
              color: INTERAX_COLORS.sun,
              flexShrink: 0,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 200ms ease'
            }}
          />
        </Box>
      </Box>

      {/* Expanded content */}
      {isExpanded && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 1 }}>
          {teamDetails.map((team, teamIdx) => (
            <Box key={`${team.teamName}-${teamIdx}`}>
              {/* Team name row */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.75
                }}
              >
                <Box className="text-body-3" sx={{ color: INTERAX_COLORS.white, fontWeight: 600 }}>
                  {team.teamName}
                </Box>
                <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                  {team.memberNames.length} {L.members}
                </Box>
              </Box>

              {/* Member avatars + names */}
              {team.memberNames.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {team.memberNames.map((name, idx) => (
                    <Box
                      key={`${name}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        backgroundColor: INTERAX_COLORS.heritageBlue,
                        border: `1px solid ${INTERAX_COLORS.white10}`,
                        borderRadius: '20px',
                        px: 1,
                        py: 0.5
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 20,
                          height: 20,
                          backgroundColor: INTERAX_COLORS.sun,
                          borderRadius: '50%',
                          flexShrink: 0
                        }}
                      >
                        <Box
                          className="text-body-3"
                          sx={{ color: INTERAX_COLORS.offBlack, fontWeight: 700, fontSize: '9px', lineHeight: 1 }}
                        >
                          {getInitials(name)}
                        </Box>
                      </Box>
                      <Box className="text-body-3" sx={{ color: INTERAX_COLORS.white, fontSize: '11px' }}>
                        {name}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, fontStyle: 'italic' }}>
                  {L.noMembersAssigned}
                </Box>
              )}
            </Box>
          ))}

          {/* Unassigned participants */}
          {unassignedMemberNames.length > 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 0.75
                }}
              >
                <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey, fontWeight: 600 }}>
                  {L.unassigned}
                </Box>
                <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
                  {unassignedMemberNames.length} {L.members}
                </Box>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {unassignedMemberNames.map((name, idx) => (
                  <Box
                    key={`unassigned-${name}-${idx}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      backgroundColor: INTERAX_COLORS.heritageBlue,
                      border: `1px solid ${INTERAX_COLORS.white10}`,
                      borderRadius: '20px',
                      px: 1,
                      py: 0.5
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 20,
                        height: 20,
                        backgroundColor: INTERAX_COLORS.thunderGrey,
                        borderRadius: '50%',
                        flexShrink: 0
                      }}
                    >
                      <Box
                        className="text-body-3"
                        sx={{ color: INTERAX_COLORS.white, fontWeight: 700, fontSize: '9px', lineHeight: 1 }}
                      >
                        {getInitials(name)}
                      </Box>
                    </Box>
                    <Box className="text-body-3" sx={{ color: INTERAX_COLORS.white70, fontSize: '11px' }}>
                      {name}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default SprintTeamMembersCollapsible
